import { Spacing } from '@persona-id/design-tokens';
import { useEffect, useState } from 'react';
import { COLORS } from 'shared/styles/variables';
import styled, { css, keyframes } from 'styled-components';

import {
  GraphExplorerQueryLoadingStage,
  GraphExplorerQueryLoadingState,
} from 'dashboard/models/graph-explorer';
import { GraphQueryHint, GraphQueryHintType } from 'dashboard/models/graph-query';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';

import PercentFillBar from '../PercentFillBar';

interface GraphExplorerLoadingOverlay {
  queryLoadingState: GraphExplorerQueryLoadingState;
  queryHint: GraphQueryHint | null;
  handleQueryCancel: () => void;
}

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: ${Z_INDICES.floatingBar + 1};
  border-radius: inherit; /* so it doesn't spill out of container */

  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Wrapper = styled.div`
  display: block;
  width: 50%;
  max-width: 500px;
  min-width: 300px;
  border-radius: 5px;

  background: ${COLORS.white};
  padding: 20px;
`;

const StyledProgressBar = styled(PercentFillBar)`
  width: 100%;
`;

const ProgressBarRow = styled('div')`
  width: 100%;
  margin-bottom: ${Spacing[4]};
`;

const ProgressDetailsRow = styled.div<{ height: number }>`
  ${({ height }) => css`
    height: ${height}px;
    transition: 0.4s ease-in-out;
  `}
`;

const textFadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const QueryHintMessage = styled(Paragraph)`
  animation: ${textFadeIn} 1s;
`;

const CancelButton = styled(Button)`
  height: fit-content;
  width: fit-content;
  padding: ${Spacing[2]} ${Spacing[4]} ${Spacing[2]};
  margin-right: 0;
  margin-left: auto;
  margin-top: ${Spacing[2]};

  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
`;

interface FillBarData {
  endPercent: number;
  estimatedDuration: number;
  label: string;
}

function loadingStateToFillBarData(loadingStage: GraphExplorerQueryLoadingStage): FillBarData {
  switch (loadingStage) {
    case GraphExplorerQueryLoadingStage.LoadingGraph:
      return {
        endPercent: 0.2,
        estimatedDuration: 3000,
        label: 'Loading Graph...',
      };
    case GraphExplorerQueryLoadingStage.SendingQuery:
      return {
        endPercent: 0.2,
        estimatedDuration: 4000,
        label: 'Sending Query...',
      };
    case GraphExplorerQueryLoadingStage.WaitingForGraphQueryResult:
      return {
        endPercent: 0.6,
        estimatedDuration: 12000,
        label: 'Executing Query...',
      };
    case GraphExplorerQueryLoadingStage.FetchingQuery:
      return {
        endPercent: 0.6,
        estimatedDuration: 5000,
        label: 'Fetching Query Results...',
      };
    case GraphExplorerQueryLoadingStage.HydratingGraphNodes:
      return {
        endPercent: 0.9,
        estimatedDuration: 3000,
        label: 'Hydrating Graph Nodes...',
      };
    case GraphExplorerQueryLoadingStage.RenderingQueryResult:
      return {
        endPercent: 0.98,
        estimatedDuration: 1000,
        label: 'Rendering Query Result...',
      };
    default:
      return {
        endPercent: 1,
        estimatedDuration: 200,
        label: 'Complete',
      };
  }
}

interface QueryHintData {
  msg: string | null;
  height: number; // how much room the row will take up with the message
}

function queryHintToData(queryHint: GraphQueryHint): QueryHintData {
  switch (queryHint?.type) {
    case GraphQueryHintType.ReduceTimeRange:
      return {
        msg: 'Execution is taking a while... to get faster results, try cancelling the query, reducing the time range, and querying again',
        height: 50,
      };
    default:
      return {
        msg: null,
        height: 0,
      };
  }
}

const GraphExplorerQueryProgressBar = ({
  queryLoadingState,
  queryHint,
  handleQueryCancel,
}: GraphExplorerLoadingOverlay) => {
  const fillBarData = loadingStateToFillBarData(queryLoadingState.stage);

  // the loading bar will jitter if its first animation does not complete, so on mount,
  // we trigger an animation from 0% to 0% that lasts 0ms.
  const [initialized, setInitialized] = useState<boolean>(false);
  useEffect(() => {
    setTimeout(() => {
      setInitialized(true);
    }, 500);
  }, []);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const queryHintData = queryHintToData(queryHint);

  return (
    <Container id='graph-explorer-query-progress-bar' data-test='graph-explorer-query-progress-bar'>
      <Wrapper>
        <ProgressBarRow>
          <StyledProgressBar
            label={
              <Paragraph size='md' color='primary'>
                {fillBarData.label}
              </Paragraph>
            }
            percent={initialized ? fillBarData.endPercent : 0}
            transitionDuration={initialized ? `${fillBarData.estimatedDuration}ms` : '0ms'}
            striped={true}
          />
        </ProgressBarRow>
        <ProgressDetailsRow height={queryHintData.height}>
          {queryHintData.msg && <QueryHintMessage size='sm'>{queryHintData.msg}</QueryHintMessage>}
        </ProgressDetailsRow>
        <CancelButton variant='basic' onClick={handleQueryCancel}>
          Cancel
        </CancelButton>
      </Wrapper>
    </Container>
  );
};

export default GraphExplorerQueryProgressBar;
